import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { be1Fetch } from '../../../../config/api';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { RootState, AppDispatch } from '../../../../store';
import { fetchDataDefinitions, fetchSamTypes, DataDefinition, SamType } from '../../../../store/dataDefinitionsSlice';
import NewDataDefinitionDialog from './NewDataDefinitionDialog';
import { showConfirm } from '../../../common/AppDialog';
import { isEnter } from '../../../../utils/keyboard';
import { useTranslation } from 'react-i18next';
import { errorFromResponse } from '../../../../services/errorText';
import { formatDateTime } from '../../../../utils/formatters';

interface Entry { name: string; path: string; type: 'dir' | 'file'; size: number | null; modified?: string | null; ext?: string }
interface Head { path: string; name: string; size: number | null; modified?: string | null; ext?: string; binary: boolean; lines: string[]; truncated: boolean }

interface Props {
  onClose: () => void;
  /** 파일을 고르는 용도로도 쓸 수 있다 (없으면 보기 전용 탐색기) */
  onSelect?: (relPath: string) => void;
}

const fmtSize = (n: number | null | undefined) => {
  if (n === null || n === undefined) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
};
const fmtTime = (s?: string | null) => (s ? formatDateTime(s) : '');   // 시간대를 맞춘다(프로젝트 시간대)
const icon = (e: Entry) => (e.type === 'dir' ? '📁' : ['csv', 'tsv', 'dat', 'txt'].includes(e.ext || '') ? '📄' : ['json', 'xml', 'yaml', 'yml'].includes(e.ext || '') ? '🧾' : ['gz', 'zip', 'tar'].includes(e.ext || '') ? '🗜' : '📦');

/**
 * 서버 폴더 탐색 — 서버(SAM_BROWSE_ROOT 하위)의 폴더/파일을 훑어보고 내용을 확인한다.
 * 왼쪽 목록(경로 이동·이름 검색) + 오른쪽 미리보기(텍스트 앞부분, CSV 는 표로).
 * 루트 밖 경로는 백엔드가 막는다.
 */
const ServerExplorerDialog: React.FC<Props> = ({ onClose, onSelect }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const dispatch = useDispatch<AppDispatch>();
  // SAM 데이터 정의와 연계 — 정의된 파일은 목록에 배지가 붙고, 클릭하면 정의를 열어 볼 수 있다
  const definitions = useSelector((st: RootState) => st.dataDefinitions.items);
  useEffect(() => { dispatch(fetchDataDefinitions()); }, [dispatch]);
  const samDefByPath = useMemo(() => {
    const m = new Map<string, DataDefinition>();
    definitions.filter((d) => d.type === 'sam' && d.source.filepath)
      .forEach((d) => m.set(String(d.source.filepath).replace(/^[/]+/, ''), d));
    return m;
  }, [definitions]);
  const [defView, setDefView] = useState<DataDefinition | null>(null);
  // 미정의 파일이 SAM유형의 경로 패턴과 일치하면 "이 유형으로 정의할까요?" 제안
  const samTypes = useSelector((st: RootState) => st.dataDefinitions.samTypes);
  useEffect(() => { dispatch(fetchSamTypes()); }, [dispatch]);
  const patternToRegex = (pat: string) => new RegExp('^' + pat.split('*').map((x) => x.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*') + '$');
  const typeForPath = (path: string): SamType | undefined =>
    samTypes.find((tp) => tp.pathPattern && patternToRegex(tp.pathPattern).test(path));
  const [createDef, setCreateDef] = useState<{ filepath: string; samTypeId: string; name: string } | null>(null);
  const [path, setPath] = useState('');
  const [parent, setParent] = useState<string | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [root, setRoot] = useState('');
  /** 체험 계정처럼 볼 수 있는 범위가 정해진 경우의 맨 위 폴더 (루트 기준 상대경로, 예: trial/trial01) */
  const [scope, setScope] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [sel, setSel] = useState<Entry | null>(null);
  const [head, setHead] = useState<Head | null>(null);
  const [headErr, setHeadErr] = useState<string | null>(null);
  const [asTable, setAsTable] = useState(true);

  const load = useCallback((p: string) => {
    setLoading(true); setError(null); setSearching(false);
    be1Fetch(`/api/fs/browse?path=${encodeURIComponent(p)}`)
      .then((r) => r.json())
      .then((d) => { setPath(d.path || ''); setParent(d.parent ?? null); setEntries(d.entries || []); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(''); }, [load]);
  useEffect(() => { be1Fetch('/api/fs/root').then((r) => r.json()).then((d) => { setRoot(d.root || ''); setScope(d.scope || null); }).catch(() => {}); }, []);

  // 파일을 고르면 앞부분을 읽어 보여 준다
  useEffect(() => {
    if (!sel || sel.type !== 'file') { setHead(null); setHeadErr(null); return; }
    setHead(null); setHeadErr(null);
    be1Fetch(`/api/fs/head?path=${encodeURIComponent(sel.path)}&lines=200`)
      .then(async (r) => { if (!r.ok) throw await errorFromResponse(r, t('datadef:explorer.previewFailed')); return r.json(); })
      .then(setHead)
      .catch((e) => setHeadErr(e.message));
  }, [sel, t]);

  const doSearch = () => {
    const q = query.trim();
    if (!q) { load(path); return; }
    setLoading(true); setError(null);
    be1Fetch(`/api/fs/search?path=${encodeURIComponent(path)}&pattern=${encodeURIComponent(q)}`)
      .then((r) => r.json())
      .then((d) => { setEntries(d.entries || []); setSearching(true); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  // 경로 표시 — 범위가 정해진 계정은 「내 폴더」 부터. 그 위(루트·trial)는 서버가 보여 주지 않으므로 눌러도 같은
  // 목록이 나와 헷갈렸다(현장 2026-09-29) — 아예 보이지 않게 한다
  const allCrumbs = ['', ...path.split('/').filter(Boolean).map((_, i, a) => a.slice(0, i + 1).join('/'))];
  const crumbs = scope ? [scope, ...allCrumbs.filter((c) => c.startsWith(`${scope}/`))] : allCrumbs;
  const open = async (e: Entry) => {
    if (e.type === 'dir') { setSel(null); setQuery(''); load(e.path); return; }
    setSel(e);
    const def = samDefByPath.get(e.path);
    if (def) {
      const yes = await showConfirm(
        t('datadef:explorer.defConfirm', { name: def.name }),
        { title: t('datadef:explorer.defTitle'), okText: t('datadef:explorer.viewDef'), cancelText: t('datadef:explorer.fileOnly') },
      );
      if (yes) setDefView(def);
      return;
    }
    const tp = typeForPath(e.path);
    if (tp) {
      const yes = await showConfirm(
        t('datadef:explorer.typeConfirm', { name: tp.name, pattern: tp.pathPattern }),
        { title: t('datadef:explorer.typeTitle'), okText: t('datadef:explorer.createDef'), cancelText: t('datadef:explorer.fileOnly') },
      );
      if (yes) setCreateDef({ filepath: e.path, samTypeId: tp.id, name: e.name.replace(/[.][^.]+$/, '') });
    }
  };

  // CSV/TSV 는 표로 보여 준다 (구분자는 확장자로 추정)
  const delim = head?.ext === 'tsv' ? '\t' : ',';
  const table = head && !head.binary && asTable && ['csv', 'tsv'].includes(head.ext || '')
    ? head.lines.slice(0, 200).map((l) => l.split(delim))
    : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-[1000px] max-w-[95vw] h-[80vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('datadef:explorer.title')}</div>
            <div className="text-[0.6875rem] text-gray-500 font-mono truncate max-w-[600px]" title={root}>{root || '…'}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('datadef:common.closeEsc')}>✕</button>
        </div>

        {/* 경로 · 검색 */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200">
          <button className="px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 disabled:opacity-40"
            disabled={parent === null || searching} onClick={() => parent !== null && load(parent)}>{t('datadef:explorer.up')}</button>
          <div className="flex items-center gap-1 text-xs text-gray-600 min-w-0 flex-1 overflow-hidden">
            {crumbs.map((c, i) => (
              <React.Fragment key={c || 'root'}>
                {i > 0 && <span className="text-gray-300">/</span>}
                <button className="hover:text-blue-600 truncate" onClick={() => { setQuery(''); load(c); }}>
                  {i === 0 ? (scope ? t('datadef:explorer.myFolder') : t('datadef:explorer.root')) : c.split('/').pop()}
                </button>
              </React.Fragment>
            ))}
          </div>
          <input className="border border-gray-300 rounded px-2 py-0.5 text-xs w-48" placeholder={t('datadef:explorer.searchPh')}
            value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (isEnter(e)) doSearch(); }} />
          <button className="px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={doSearch}>{t('datadef:explorer.find')}</button>
          <button className="px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={() => { setQuery(''); load(path); }}>{t('common:ui.refresh')}</button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-[420px_1fr] divide-x divide-gray-200">
          {/* 목록 */}
          <div className="overflow-auto">
            {searching && <div className="px-3 py-1 text-[0.6875rem] text-blue-600 bg-blue-50">{t('datadef:explorer.searchResult', { n: entries.length })}</div>}
            {loading ? <div className="p-3 text-xs text-gray-500">{t('datadef:common.reading')}</div>
              : error ? <div className="p-3 text-xs text-red-500">{error}</div>
                : entries.length === 0 ? <div className="p-3 text-xs text-gray-500">{t('datadef:explorer.empty')}</div>
                  : entries.map((e) => (
                    <button key={e.path} type="button" onClick={() => open(e)} onDoubleClick={() => e.type === 'file' && onSelect?.(e.path)}
                      className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${sel?.path === e.path ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'}`}>
                      <span>{icon(e)}</span>
                      <span className="flex-1 truncate" title={e.path}>{searching ? e.path : e.name}</span>
                      {samDefByPath.has(e.path) ? (
                        <span className="shrink-0 text-[0.625rem] px-1 rounded bg-blue-100 text-blue-700"
                          title={t('datadef:explorer.samBadgeTitle', { name: samDefByPath.get(e.path)!.name })}>SAM</span>
                      ) : e.type === 'file' && typeForPath(e.path) ? (
                        <span className="shrink-0 text-[0.625rem] px-1 rounded bg-amber-100 text-amber-700"
                          title={t('datadef:explorer.typeBadgeTitle', { name: typeForPath(e.path)!.name })}>{t('datadef:explorer.typeBadge')}</span>
                      ) : null}
                      <span className="text-[0.6875rem] text-gray-400 shrink-0">{fmtSize(e.size)}</span>
                      <span className="text-[0.6875rem] text-gray-400 shrink-0 w-[112px] text-right">{fmtTime(e.modified)}</span>
                    </button>
                  ))}
          </div>

          {/* 미리보기 */}
          <div className="overflow-auto p-3 min-h-0">
            {!sel ? (
              <div className="text-xs text-gray-400">{t('datadef:explorer.pickHint')}</div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-sm font-semibold text-gray-800 truncate" title={sel.path}>{sel.name}</div>
                  <div className="text-[0.6875rem] text-gray-500">{fmtSize(sel.size)} · {fmtTime(sel.modified)}</div>
                  <div className="ml-auto flex items-center gap-1.5">
                    {samDefByPath.has(sel.path) && (
                      <button className="px-2 py-0.5 text-[0.6875rem] rounded border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700"
                        onClick={() => setDefView(samDefByPath.get(sel.path)!)}>{t('datadef:explorer.viewSamDef')}</button>
                    )}
                    {['csv', 'tsv'].includes(head?.ext || '') && !head?.binary && (
                      <button className="px-2 py-0.5 text-[0.6875rem] rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
                        onClick={() => setAsTable(!asTable)}>{asTable ? t('datadef:explorer.raw') : t('datadef:explorer.asTable')}</button>
                    )}
                    <a className="px-2 py-0.5 text-[0.6875rem] rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
                      href={`/api/fs/download?path=${encodeURIComponent(sel.path)}`} target="_blank" rel="noreferrer">{t('datadef:explorer.download')}</a>
                    {onSelect && (
                      <button className="px-2 py-0.5 text-[0.6875rem] rounded bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => onSelect(sel.path)}>{t('datadef:explorer.selectFile')}</button>
                    )}
                  </div>
                </div>
                <div className="text-[0.6875rem] text-gray-400 font-mono mb-2 truncate">{sel.path}</div>
                {headErr ? <div className="text-xs text-red-500">{headErr}</div>
                  : !head ? <div className="text-xs text-gray-500">{t('datadef:common.reading')}</div>
                    : head.binary ? <div className="text-xs text-gray-500">{t('datadef:explorer.binary')}</div>
                      : table ? (
                        <div className="overflow-auto border border-gray-200 rounded">
                          <table className="text-xs w-full">
                            <tbody>
                              {table.map((cells, ri) => (
                                <tr key={ri} className={ri === 0 ? 'bg-gray-50 font-medium' : ''}>
                                  <td className="px-1.5 py-0.5 text-gray-300 text-right border-r border-gray-100 select-none">{ri === 0 ? '' : ri}</td>
                                  {cells.map((c, ci) => <td key={ci} className="px-1.5 py-0.5 border-b border-gray-100 whitespace-nowrap">{c}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <pre className="text-[0.6875rem] font-mono whitespace-pre bg-gray-50 border border-gray-200 rounded p-2 overflow-auto">{head.lines.join('\n')}</pre>
                      )}
                {head?.truncated && <div className="text-[0.6875rem] text-gray-400 mt-1">{t('datadef:explorer.truncated')}</div>}
              </>
            )}
          </div>
        </div>

        <div className="px-4 py-2.5 border-t border-gray-200 flex items-center gap-2">
          <span className="text-[0.6875rem] text-gray-500 mr-auto">{t('datadef:explorer.footer', { n: entries.length })}{onSelect ? t('datadef:explorer.footerSelect') : ''} · <span className="px-1 rounded bg-blue-100 text-blue-700">SAM</span>{t('datadef:explorer.footerSam')}</span>
          <button className="px-3 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={onClose}>{t('common:ui.close')}</button>
        </div>
      </div>

      {/* 중첩 다이얼로그 — 바깥 오버레이의 onMouseDown(닫기)로 버블링되지 않게 막는다 */}
      {createDef && (
        <div onMouseDown={(ev) => ev.stopPropagation()}>
          <NewDataDefinitionDialog type="sam" folderPath="sam" initial={createDef}
            onClose={() => { setCreateDef(null); dispatch(fetchDataDefinitions()); }} />
        </div>
      )}

      {/* SAM파일 정의 요약 — 형식 옵션과 항목 레이아웃 */}
      {defView && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={() => setDefView(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-[520px] max-w-[92vw] max-h-[70vh] flex flex-col" onMouseDown={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div>
                <div className="text-sm font-semibold text-gray-800">{t('datadef:explorer.defViewTitle', { name: defView.name })}</div>
                <div className="text-[0.6875rem] text-gray-500 font-mono">{defView.source.filepath}</div>
              </div>
              <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={() => setDefView(null)}>✕</button>
            </div>
            <div className="px-4 py-2 text-xs text-gray-700 border-b border-gray-100 flex flex-wrap gap-x-4 gap-y-1">
              <span>{t('datadef:explorer.formatLabel')}<b>{(defView.source as any).format === 'fixed' ? t('datadef:sam.fixed') : t('datadef:sam.delimited')}</b></span>
              {(defView.source as any).format === 'fixed'
                ? <span>{t('datadef:explorer.widthsLabel')}<b className="font-mono">{(defView.source as any).fieldWidths || '-'}</b></span>
                : <span>{t('datadef:explorer.delimiterLabel')}<b className="font-mono">{defView.source.delimiter === String.fromCharCode(9) ? 'TAB' : (defView.source.delimiter || ',')}</b></span>}
              <span>{t('datadef:explorer.headerLabel')}<b>{defView.source.hasHeader === false ? t('datadef:new.no') : t('datadef:new.yes')}</b></span>
              <span>{t('datadef:explorer.encodingLabel')}<b>{(defView.source as any).encoding || 'UTF-8'}</b></span>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-3">
              <table className="text-xs w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{t('datadef:explorer.field')}</th>
                    <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{t('datadef:explorer.dataType')}</th>
                  </tr>
                </thead>
                <tbody>
                  {defView.columns.map((c) => (
                    <tr key={c.name}><td className="px-2 py-0.5 border-b border-gray-100">{c.name}</td><td className="px-2 py-0.5 border-b border-gray-100 text-gray-500">{c.dataType}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 border-t border-gray-200 flex justify-end">
              <button className="px-3 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={() => setDefView(null)}>{t('common:ui.close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerExplorerDialog;
